import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
    AnalyticsDrilldownLevel1Args,
    AnalyticsDrilldownLevel2Args,
    buildAnalyticsDrilldownLevel1Request,
    buildAnalyticsDrilldownLevel2Request,
    BuildAnalyticsRequestArgs,
    buildAnalyticsAIRequest,
    buildAnalyticsDashboardsRequest,
    buildAnalyticsRequest,
    buildAnalyticsTilesRequest,
    fetchAnalyticsDrilldownLevel1Request,
    fetchAnalyticsDrilldownLevel2Request,
    fetchAnalyticsAIRequest,
    fetchAnalyticsDashboardsRequest,
    fetchAnalyticsRequest,
    fetchAnalyticsTilesRequest,
} from 'services/analyticsQueryService'

interface UseAnalyticsDataProps {
    requestArgs: BuildAnalyticsRequestArgs | null
    enabled?: boolean
}

export function useAnalyticsData(props: UseAnalyticsDataProps) {
    const { requestArgs, enabled = true } = props

    const request = useMemo(() => {
        if (!requestArgs) {
            return null
        }

        return buildAnalyticsRequest(requestArgs)
    }, [requestArgs])

    const query = useQuery({
        queryKey: request?.queryKey || ['analytics-data', 'disabled'],
        enabled: Boolean(request) && enabled,
        queryFn: ({ signal }) => fetchAnalyticsRequest(request, requestArgs?.app, signal),
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: 1,
        placeholderData: (previousData) => previousData,
    })

    return {
        ...query,
        request,
    }
}

export function useAnalyticsAIData(props: UseAnalyticsDataProps) {
    const { requestArgs, enabled = true } = props

    const request = useMemo(() => {
        if (!requestArgs) {
            return null
        }

        return buildAnalyticsAIRequest(requestArgs)
    }, [requestArgs])

    const query = useQuery({
        queryKey: request?.queryKey || ['analytics-ai', 'disabled'],
        enabled: Boolean(request) && enabled,
        queryFn: ({ signal }) => fetchAnalyticsAIRequest(request, requestArgs?.app, signal),
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: 1,
        placeholderData: (previousData) => previousData,
    })

    return {
        ...query,
        request,
    }
}

export function useAnalyticsDrilldownLevel1(props: { requestArgs: AnalyticsDrilldownLevel1Args | null, enabled?: boolean }) {
    const { requestArgs, enabled = true } = props

    const request = useMemo(() => {
        if (!requestArgs) {
            return null
        }
        return buildAnalyticsDrilldownLevel1Request(requestArgs)
    }, [requestArgs])

    const query = useQuery({
        queryKey: request?.queryKey || ['analytics-drilldown-l1', 'disabled'],
        enabled: Boolean(request) && enabled,
        queryFn: ({ signal }) => fetchAnalyticsDrilldownLevel1Request(request, requestArgs?.app, signal),
        staleTime: 2 * 60 * 1000,
        gcTime: 15 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: 1,
        placeholderData: (previousData) => previousData,
    })

    return {
        ...query,
        request,
    }
}

export function useAnalyticsDrilldownLevel2(props: { requestArgs: AnalyticsDrilldownLevel2Args | null, enabled?: boolean }) {
    const { requestArgs, enabled = true } = props

    const request = useMemo(() => {
        if (!requestArgs) {
            return null
        }
        return buildAnalyticsDrilldownLevel2Request(requestArgs)
    }, [requestArgs])

    const query = useQuery({
        queryKey: request?.queryKey || ['analytics-drilldown-l2', 'disabled'],
        enabled: Boolean(request) && enabled,
        queryFn: ({ signal }) => fetchAnalyticsDrilldownLevel2Request(request, requestArgs?.app, signal),
        staleTime: 2 * 60 * 1000,
        gcTime: 15 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: 1,
        placeholderData: (previousData) => previousData,
    })

    return {
        ...query,
        request,
    }
}

export function useAnalyticsDashboards(props: { appId?: string, organizationId?: string, enabled?: boolean }) {
    const { appId, organizationId, enabled = true } = props

    const request = useMemo(
        () => buildAnalyticsDashboardsRequest({ appId, organizationId }),
        [appId, organizationId],
    )

    const query = useQuery({
        queryKey: request.queryKey,
        enabled,
        queryFn: () => fetchAnalyticsDashboardsRequest(request),
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: 1,
        placeholderData: (previousData) => previousData,
    })

    return {
        ...query,
        request,
    }
}

export function useAnalyticsTiles(props: { dashboardId?: string, enabled?: boolean }) {
    const { dashboardId, enabled = true } = props

    const request = useMemo(
        () => (dashboardId ? buildAnalyticsTilesRequest({ dashboardId }) : null),
        [dashboardId],
    )

    const query = useQuery({
        queryKey: request?.queryKey || ['analytics-dashboard-tiles', 'disabled'],
        enabled: Boolean(request) && enabled,
        queryFn: () => fetchAnalyticsTilesRequest(request),
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: 1,
        placeholderData: (previousData) => previousData,
    })

    return {
        ...query,
        request,
    }
}